import { apiDelete, apiGet, apiPost, apiPut } from "../client";
import type {
  CreateSubcategoryRequestApi,
  ParentCategoryListItemApi,
  ParentCategoryListResponseApi,
  SubcategoryListResponseApi,
  TransactionCategoryListResponseApi,
  TransactionCategoryResponseApi,
  UpdateSubcategoryRequestApi,
} from "../models/transactionCategory";

export async function getAllTransactionCategories(): Promise<TransactionCategoryListResponseApi> {
  const [parentResponse, subResponse] = await Promise.all([
    getParentCategories({ pageSize: 500, isActive: true }),
    getSubcategories({ pageSize: 500, isActive: true }),
  ]);

  const now = new Date().toISOString();

  const parentItems = parentResponse.data.map((p) => ({
    id: p.id,
    name: p.name,
    createdAt: now,
    updatedAt: now,
  }));

  const subItems = subResponse.data.map((s) => ({
    id: s.id,
    name: s.name,
    createdAt: now,
    updatedAt: now,
  }));

  const merged = [...parentItems, ...subItems].sort((a, b) =>
    a.name.localeCompare(b.name),
  );

  return {
    categories: merged,
    totalCount: merged.length,
  };
}

// --- Parent/subcategory CRUD ---

export async function getParentCategories(params?: {
  name?: string;
  isActive?: boolean;
  pageNumber?: number;
  pageSize?: number;
}): Promise<ParentCategoryListResponseApi> {
  const searchParams = new URLSearchParams();
  if (params?.name) searchParams.append("name", params.name);
  if (params?.isActive !== undefined)
    searchParams.append("isActive", String(params.isActive));
  searchParams.append("pageNumber", String(params?.pageNumber ?? 1));
  searchParams.append("pageSize", String(params?.pageSize ?? 500));

  const query = searchParams.toString();
  return apiGet<ParentCategoryListResponseApi>(
    `transaction-categories/parent?${query}`,
  );
}

export async function getSubcategories(params?: {
  name?: string;
  isActive?: boolean;
  parentCategoryId?: number;
  parentCategoryName?: string;
  pageNumber?: number;
  pageSize?: number;
}): Promise<SubcategoryListResponseApi> {
  const searchParams = new URLSearchParams();
  if (params?.name) searchParams.append("name", params.name);
  if (params?.isActive !== undefined)
    searchParams.append("isActive", String(params.isActive));
  if (params?.parentCategoryId !== undefined)
    searchParams.append("parentCategoryId", String(params.parentCategoryId));
  if (params?.parentCategoryName)
    searchParams.append("parentCategoryName", params.parentCategoryName);
  searchParams.append("pageNumber", String(params?.pageNumber ?? 1));
  searchParams.append("pageSize", String(params?.pageSize ?? 500));

  const query = searchParams.toString();
  return apiGet<SubcategoryListResponseApi>(
    `transaction-categories/sub?${query}`,
  );
}

export async function createSubcategory(
  request: CreateSubcategoryRequestApi,
): Promise<TransactionCategoryResponseApi> {
  return apiPost<TransactionCategoryResponseApi>(
    "transaction-categories/sub",
    request,
  );
}

export async function updateSubcategory(
  categoryId: number,
  request: UpdateSubcategoryRequestApi,
): Promise<TransactionCategoryResponseApi> {
  return apiPut<TransactionCategoryResponseApi>(
    `transaction-categories/sub/${categoryId}`,
    request,
  );
}

export async function deleteSubcategory(categoryId: number): Promise<void> {
  return apiDelete<void>(`transaction-categories/sub/${categoryId}`);
}
